import React, { useCallback, useEffect } from 'react';
import { Handle, Position, useStore, useUpdateNodeInternals } from 'reactflow';
import { Typography } from 'antd';
import { NODE_DEFINITIONS, getNodeIcon } from '../../utils/nodeTypes';

const { Text } = Typography;

const CustomNode = React.memo(({ id, data, selected }) => {
  // Use standardized nodeType from data - with multiple fallback options
  const nodeType = data.nodeType || data.type;
  
  // Hook to force ReactFlow to recalculate handle positions
  const updateNodeInternals = useUpdateNodeInternals();
  
  // Check if this node is currently being processed
  const isProcessing = data.isProcessing || false;
  const isCompleted = data.isCompleted || false;
  const hasError = data.hasError || false;
  const isDebugTarget = data.isDebugTarget || false;
  const isDebugPaused = data.isDebugPaused || false;
  
  // Enhanced node definition lookup with better error handling
  const nodeDef = NODE_DEFINITIONS[nodeType] || {
    name: nodeType ? nodeType.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase()) : 'Unknown Node',
    inputs: nodeType === 'start' ? [] : ['flow'],
    outputs: nodeType === 'end' ? [] : ['flow']
  };

  // Get connected edges count for this node (efficiently)
  const connectedEdgesSelector = useCallback((store) => {
    return store.edges.filter(e => e.target === id).length;
  }, [id]);
  
  const connectedCount = useStore(connectedEdgesSelector);

  // Calculate dynamic inputs
  const inputs = React.useMemo(() => {
    // Handle batch_start dynamic inputs
    if (nodeType === 'batch_start') {
      const config = data.config || {};
      const dynamicInputs = config.dynamicInputs || [];
      // Always have at least one input
      if (dynamicInputs.length === 0) {
        return ['input_1'];
      }
      return dynamicInputs.map(inp => inp.id || inp);
    }
    
    if (nodeType === 'export_excel') {
      const config = data.config || {};
      if (!config.useDynamicInput && config.sheets && config.sheets.length > 0) {
        return config.sheets.map(s => s.id || s.name);
      }
    }
    return nodeDef.inputs;
  }, [nodeType, data.config, nodeDef.inputs, data]);

  // Calculate dynamic outputs for batch_end
  const outputs = React.useMemo(() => {
    if (nodeType === 'batch_end') {
      const config = data.config || {};
      const dynamicOutputs = config.dynamicOutputs || [];
      // Outputs are synced from paired batch_start
      if (dynamicOutputs.length === 0) {
        return ['output_1'];
      }
      return dynamicOutputs.map(out => out.id || out);
    }
    return nodeDef.outputs;
  }, [nodeType, data.config, nodeDef.outputs, data]);

  // Force ReactFlow to recalculate handle positions when handle count changes
  useEffect(() => {
    if (nodeType === 'batch_start' || nodeType === 'batch_end') {
      // Immediate update first (no delay)
      updateNodeInternals(id);
      
      // Then a delayed update to ensure DOM is fully rendered
      const timer = setTimeout(() => {
        updateNodeInternals(id);
      }, 100);
      return () => clearTimeout(timer);
    }
  }, [id, nodeType, inputs.length, outputs.length, updateNodeInternals]);

  const getNodeLabel = () => {
    const config = data.config || {};
    
    // Priority order: customName > label > specific config > default
    if (data.customName) {
      return data.customName;
    }
    
    // Check if there's a custom label
    if (data.label) {
      return data.label;
    }

    // Generate label based on node type and config
    switch (nodeType) {
      case 'start':
        return 'Start';
      case 'end':
        return 'End';
      case 'input':
        return config.label || 'Input Field';
      case 'dataset_input':
        return config.tableName || 'Dataset Input';
      case 'dataset_output':
        return config.outputName || 'Dataset Output';
      case 'table':
        return config.tableName || data.tableName || 'Table';
      case 'csv_input':
        return config.fileName || data.fileName || 'CSV Input';
      case 'excel_input':
        return config.fileName || data.fileName || 'Excel Input';
      case 'filter_rows':
        return config.condition ? `Filter: ${config.condition}` : 'Filter Rows';
      case 'add_column':
        return config.columnName ? `Add: ${config.columnName}` : 'Add Column';
      case 'remove_column':
        return config.columnName ? `Remove: ${config.columnName}` : 'Remove Column';
      case 'export_excel':
        return config.fileName ? `${config.fileName}.xlsx` : 'Export Excel';
      case 'set_dataset_name':
        return config.datasetName || 'Set Dataset Name';
      case 'batch_start':
        return data.label || 'Batch Start';
      case 'batch_end':
        return data.label || 'Batch End';
      default:
        return nodeDef.name || nodeType.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
    }
  };

  const getSubLabel = () => {
    const config = data.config || {};
    
    switch (nodeType) {
      case 'start':
        return 'Flow Start';
      case 'end':
        return 'Flow End';
      case 'input':
        return `${config.inputType || 'text'} input`;
      case 'table':
        return config.selectedSchema ? 
          `${config.selectedSchema.filter(col => col.enabled !== false).length} columns` : 
          (data.selectedSchema ? 
            `${data.selectedSchema.filter(col => col.enabled !== false).length} columns` :
            'Not configured');
      case 'csv_input':
        return config.totalRows ? `${config.totalRows} rows` : 'CSV File';
      case 'excel_input':
        return config.totalSheets && config.totalRows ? 
          `${config.totalSheets} sheets, ${config.totalRows} rows` : 
          (data.totalSheets && data.totalRows ? 
            `${data.totalSheets} sheets, ${data.totalRows} rows` : 
            'Excel File');
      case 'filter_rows':
        return config.operator ? `${config.field} ${config.operator} ${config.value}` : 'Table Filter';
      case 'add_column':
        return config.formula ? `= ${config.formula}` : 'Column calculation';
      case 'export_excel':
        return config.useDynamicInput ? 'Dynamic Sheets' : `${connectedCount} sheets`;
      case 'set_dataset_name':
        return config.datasetName ? `Sheet: ${config.datasetName}` : 'Tag dataset';
      case 'batch_start': {
        const inputCount = inputs.length;
        const mode = config.executionMode || 'sequential';
        return `${inputCount} input${inputCount > 1 ? 's' : ''} • ${mode}`;
      }
      case 'batch_end': {
        const outputCount = outputs.length;
        return `${outputCount} output${outputCount > 1 ? 's' : ''}`;
      }
      default:
        return 'Table Format';
    }
  };

  // Calculate dynamic node height for batch nodes based on handle count
  const nodeHeight = React.useMemo(() => {
    const baseHeight = 56;
    const handleSpacing = 28; // pixels per handle spacing
    const topPadding = 18; // padding from top
    const bottomPadding = 18; // padding from bottom
    
    if (nodeType === 'batch_start') {
      const handleCount = inputs.length;
      if (handleCount > 1) {
        // Calculate height: top padding + (handles-1) * spacing + bottom padding
        return Math.max(baseHeight, topPadding + (handleCount - 1) * handleSpacing + bottomPadding);
      }
    }
    
    if (nodeType === 'batch_end') {
      const handleCount = outputs.length;
      if (handleCount > 1) {
        // Calculate height: top padding + (handles-1) * spacing + bottom padding
        return Math.max(baseHeight, topPadding + (handleCount - 1) * handleSpacing + bottomPadding);
      }
    }
    
    return baseHeight;
  }, [nodeType, inputs.length, outputs.length]);

  // For batch nodes, we need explicit dimensions on wrapper for handle positioning
  const isBatchNode = nodeType === 'batch_start' || nodeType === 'batch_end';

  // Default handle style for normal nodes
  const defaultInputStyle = {
    width: 10,
    height: 6,
    borderRadius: '1px',
    border: '1px solid #8c8c8c',
    background: '#8c8c8c',
  };

  const defaultOutputStyle = {
    width: 8,
    height: 8,
    borderRadius: '50%',
    border: '1px solid #8c8c8c',
    background: '#8c8c8c',
  };

  // Get handle style - only custom positioning for batch nodes
  const getHandleStyle = (index, total, isInput) => {
    const baseStyle = isInput ? defaultInputStyle : defaultOutputStyle;
    
    // For non-batch nodes with multiple handles, apply positioning
    if (!isBatchNode) {
      // If node has multiple inputs or outputs, position them vertically
      if (total > 1) {
        const percentage = (index + 1) / (total + 1);
        return {
          ...baseStyle,
          position: 'absolute',
          top: `${percentage * 100}%`,
          left: isInput ? -5 : 'auto',
          right: isInput ? 'auto' : -4,
          transform: 'translateY(-50%)',
        };
      }
      return baseStyle;
    }
    
    // For batch nodes with multiple handles, distribute evenly
    if (total === 1) {
      // Single handle: center it
      return {
        ...baseStyle,
        position: 'absolute',
        top: '50%',
        left: isInput ? -5 : 'auto',
        right: isInput ? 'auto' : -4,
        transform: 'translateY(-50%)',
      };
    }
    
    // For batch nodes with multiple handles: use percentage-based distribution
    // This ensures handles are always evenly distributed regardless of node height
    const percentage = (index + 1) / (total + 1);
    const topPercentage = `${percentage * 100}%`;
    
    return {
      ...baseStyle,
      position: 'absolute',
      top: topPercentage,
      left: isInput ? -5 : 'auto',
      right: isInput ? 'auto' : -4,
      transform: 'translateY(-50%)',
    };
  };

  return (
    <div
      style={{
        position: 'relative',
        cursor: 'pointer',
        width: '56px', // Always set width to prevent collapse
        height: isBatchNode ? `${nodeHeight}px` : '56px',
      }}
      data-input-count={inputs.length}
      data-output-count={outputs.length}
      data-node-height={nodeHeight}
    >
      {/* Input Handles */}
      {inputs.map((input, index) => {
        const handleId = typeof input === 'string' ? input : (input.id || `input_${index}`);
        return (
          <Handle
            key={handleId}
            type="target"
            position={Position.Left}
            id={handleId}
            isConnectable={true}
            style={getHandleStyle(index, inputs.length, true)}
          />
        );
      })}

      {/* Node Container */}
      <div
        className={hasError ? 'error-node' : isProcessing ? 'processing-node' : ''}
        style={{
          width: '56px',
          height: `${nodeHeight}px`,
          background: '#ffffff',
          border: isDebugPaused
            ? '2px solid #fa8c16'
            : hasError 
              ? '2px solid #ff4d4f'
              : isProcessing 
                ? '2px solid #1890ff' 
                : selected 
                  ? '2px solid #4096ff'
                  : isCompleted 
                    ? '2px solid #52c41a'
                    : isDebugTarget
                      ? '2px solid #fa8c16'
                      : '1px solid #d9d9d9',
          borderRadius: '12px',
          boxShadow: isDebugPaused
            ? '0 0 20px rgba(250, 140, 22, 0.6), 0 0 40px rgba(250, 140, 22, 0.4), 0 0 60px rgba(250, 140, 22, 0.2)'
            : hasError
              ? '0 0 20px rgba(255, 77, 79, 0.6), 0 0 40px rgba(255, 77, 79, 0.4), 0 0 60px rgba(255, 77, 79, 0.2)'
              : isProcessing 
                ? '0 0 20px rgba(24, 144, 255, 0.6), 0 0 40px rgba(24, 144, 255, 0.4), 0 0 60px rgba(24, 144, 255, 0.2)'
                : selected 
                  ? '0 0 0 3px rgba(64, 150, 255, 0.3)'
                  : isCompleted 
                    ? '0 0 10px rgba(82, 196, 26, 0.3)'
                    : isDebugTarget
                      ? '0 0 15px rgba(250, 140, 22, 0.4), 0 0 30px rgba(250, 140, 22, 0.2)'
                      : '0 2px 8px rgba(0, 0, 0, 0.45)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          position: 'relative',
          // Only transition specific properties to avoid GPU overhead on drag
          // 'all' causes performance issues with many nodes
          transition: 'border-color 0.2s ease, box-shadow 0.2s ease',
          animation: isDebugPaused 
            ? 'debug-pulse 1.5s ease-in-out infinite' 
            : hasError
              ? 'none'
              : isProcessing 
                ? 'pulse-glow 1.5s ease-in-out infinite' 
                : 'none'
        }}
      >
        <div
          style={{
            color: isDebugPaused
              ? '#fa8c16'
              : hasError 
                ? '#ff4d4f'
                : isProcessing 
                  ? '#1890ff' 
                  : selected 
                    ? '#4096ff'
                    : isCompleted 
                      ? '#52c41a' 
                      : isDebugTarget
                        ? '#fa8c16'
                        : '#8c8c8c',
            fontSize: '24px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            transition: 'color 0.2s ease'
          }}
        >
          {getNodeIcon(nodeType)}
        </div>
      </div>

      {/* Node Label */}
      <div
        style={{
          position: 'absolute',
          top: `${nodeHeight + 12}px`,
          left: '50%',
          transform: 'translateX(-50%)',
          textAlign: 'center',
          minWidth: '190px',
          maxWidth: '190px',
          wordBreak: 'break-word',
          whiteSpace: 'normal',
          lineHeight: '1.2'
        }}
      >
        <Text style={{ 
          fontSize: '12px', 
          fontWeight: 600, 
          color: selected ? '#4096ff' : '#262626',
          display: 'block',
          transition: 'color 0.2s ease',
          marginBottom: '2px'
        }}>
          {getNodeLabel()}
        </Text>
        <Text style={{ 
          fontSize: '10px', 
          color: '#8c8c8c',
          display: 'block',
          wordBreak: 'break-word',
          whiteSpace: 'normal'
        }}>
          {getSubLabel()}
        </Text>
      </div>

      {/* Output Handles */}
      {outputs.map((output, index) => {
        const handleId = typeof output === 'string' ? output : (output.id || `output_${index}`);
        return (
          <Handle
            key={handleId}
            type="source"
            position={Position.Right}
            id={handleId}
            isConnectable={true}
            style={getHandleStyle(index, outputs.length, false)}
          />
        );
      })}
    </div>
  );
}, (prevProps, nextProps) => {
  // Custom comparison để tránh re-render không cần thiết
  return prevProps.id === nextProps.id &&
         JSON.stringify(prevProps.data) === JSON.stringify(nextProps.data) &&
         prevProps.selected === nextProps.selected &&
         prevProps.data?.isProcessing === nextProps.data?.isProcessing &&
         prevProps.data?.isCompleted === nextProps.data?.isCompleted &&
         prevProps.data?.hasError === nextProps.data?.hasError &&
         prevProps.data?.isDebugTarget === nextProps.data?.isDebugTarget &&
         prevProps.data?.isDebugPaused === nextProps.data?.isDebugPaused;
});

export default CustomNode;